import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import employeeForm6 from '../../assets/images/employees/employeeform6.png';

const FamilyInfoForm = ({ onSubmit, data, enableValidation = false }) => {
    const [familyMembers, setFamilyMembers] = useState(data?.members || []);
    const [editIndex, setEditIndex] = useState(-1);
    const { register, handleSubmit, reset, formState: { errors } } = useForm({
        defaultValues: editIndex >= 0 ? familyMembers[editIndex] : {}
    });

    useEffect(() => {
        setFamilyMembers(data?.members || []);
        setEditIndex(-1);
        reset({});
    }, [data, reset]);

    const relations = ["Father", "Mother", "Spouse", "Child", "Sibling"];

    const getValidationRules = (field) => {
        if (!enableValidation) return {};

        const rules = {
            relation: {
                required: 'Please select a relation'
            },
            name: {
                required: 'Name is required',
                validate: (value) => {
                    if (!value) return true;
                    return /^[a-zA-Z\s]+$/.test(value.trim()) || 'Please enter a valid name';
                }
            },
            mobile: {
                validate: (value) => {
                    if (!value) return true;
                    return /^[0-9]{10}$/.test(value) || 'Please enter a valid 10-digit mobile number';
                }
            },
            dob: {}
        };

        return rules[field] || {};
    };

    const buildUpdatedMembers = (memberData) => {
        const normalizedMember = {
            relation: memberData.relation || '',
            name: memberData.name || '',
            mobile: memberData.mobile || '',
            dob: memberData.dob || '',
        };

        if (editIndex >= 0) {
            const existingMember = familyMembers[editIndex] || {};
            const updatedMembers = [...familyMembers];
            updatedMembers[editIndex] = {
                ...existingMember,
                ...normalizedMember,
            };
            return updatedMembers;
        }

        return [...familyMembers, normalizedMember];
    };

    const handleAddMember = (memberData) => {
        const updatedMembers = buildUpdatedMembers(memberData);
        setFamilyMembers(updatedMembers);
        setEditIndex(-1);
        reset({});
    };

    const handleEdit = (index) => {
        setEditIndex(index);
        reset(familyMembers[index]);
    };

    const handleDelete = (index) => {
        setFamilyMembers(familyMembers.filter((_, i) => i !== index));
        if (index === editIndex) {
            setEditIndex(-1);
            reset({});
        }
    };

    const mergeWithExisting = (values = {}) => ({
        ...(typeof data === 'object' && data !== null ? data : {}),
        ...values,
    });

    const handleNextStep = () => {
        const hasSavedMembers = familyMembers.length > 0;
        const isEditingMember = editIndex >= 0;

        if (!enableValidation || (hasSavedMembers && !isEditingMember)) {
            setEditIndex(-1);
            reset({});
            onSubmit({ family: mergeWithExisting({ members: familyMembers }) });
            return;
        }

        handleSubmit((memberData) => {
            const updatedMembers = buildUpdatedMembers(memberData);
            setFamilyMembers(updatedMembers);
            setEditIndex(-1);
            reset({});
            onSubmit({ family: mergeWithExisting({ members: updatedMembers }) });
        })();
    };

    const handleFormSubmit = (event) => {
        event.preventDefault();
        handleNextStep();
    };

    return (
        <form onSubmit={handleFormSubmit} className="d-flex flex-column" style={{ minHeight: '80vh' }}>
            <div className="row flex-grow-1">
                <div className="col-md-8">
                    <div className="row g-4 mb-4">
                        <div className="col-12">
                            <div className="form-floating">
                                <select
                                    className={`form-select ${errors.relation ? 'is-invalid' : ''}`}
                                    {...register('relation', getValidationRules('relation'))}
                                >
                                    <option value="">Select Relation</option>
                                    {relations.map(relation => (
                                        <option key={relation} value={relation.toLowerCase()}>{relation}</option>
                                    ))}
                                </select>
                                <label>Select Relation</label>
                                {errors.relation && <div className="invalid-feedback">{errors.relation.message}</div>}
                            </div>
                        </div>

                        <div className="col-12">
                            <div className="form-floating">
                                <input
                                    type="text"
                                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                    placeholder="Enter Name"
                                    {...register('name', getValidationRules('name'))}
                                />
                                <label>Name</label>
                                {errors.name && <div className="invalid-feedback">{errors.name.message}</div>}
                            </div>
                        </div>

                        <div className="col-md-6">
                            <div className="form-floating">
                                <input
                                    type="tel"
                                    className={`form-control ${errors.mobile ? 'is-invalid' : ''}`}
                                    placeholder="Enter Mobile Number"
                                    {...register('mobile', getValidationRules('mobile'))}
                                />
                                <label>Mobile Number</label>
                                {errors.mobile && <div className="invalid-feedback">{errors.mobile.message}</div>}
                            </div>
                        </div>

                        <div className="col-md-6">
                            <div className="form-floating">
                                <input
                                    type="date"
                                    className={`form-control ${errors.dob ? 'is-invalid' : ''}`}
                                    placeholder="Date of Birth"
                                    {...register('dob', getValidationRules('dob'))}
                                />
                                <label>Date of Birth</label>
                                {errors.dob && <div className="invalid-feedback">{errors.dob.message}</div>}
                            </div>
                        </div>

                        <div className="col-12">
                            <button
                                type="button"
                                className="btn btn-primary w-100"
                                onClick={handleSubmit(handleAddMember)}
                            >
                                {editIndex >= 0 ? 'Update' : 'Add'} Family Member
                            </button>
                        </div>
                    </div>

                    {familyMembers.length > 0 && (
                        <div className="table-responsive">
                            <table className="table table-bordered">
                                <thead>
                                    <tr>
                                        <th>Relation</th>
                                        <th>Name</th>
                                        <th>Mobile</th>
                                        <th>DOB</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {familyMembers.map((member, index) => (
                                        <tr key={index}>
                                            <td>{member.relation}</td>
                                            <td>{member.name}</td>
                                            <td>{member.mobile}</td>
                                            <td>{member.dob}</td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-warning me-2"
                                                    onClick={() => handleEdit(index)}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-danger"
                                                    onClick={() => handleDelete(index)}
                                                >
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <div className="col-md-4">
                    <div className="position-fixed end-0 d-none d-md-block" style={{
                        maxWidth: '400px',
                        zIndex: 1,
                        bottom: '100px'
                    }}>
                        <img
                            src={employeeForm6}
                            alt="Family Information"
                            className="img-fluid"
                            style={{ maxHeight: '45vh' }}
                        />
                    </div>
                </div>
            </div>

            <div className="mt-auto pt-4 d-flex justify-content-between">
                <button
                    type="button"
                    onClick={() => onSubmit({ family: mergeWithExisting({ members: familyMembers }), goBack: true })}
                    className="btn btn-outline-secondary px-4"
                >
                    Back
                </button>
                <button type="button" onClick={handleNextStep} className="btn btn-danger px-4">
                    Next
                </button>
            </div>
        </form>
    );
};

export default FamilyInfoForm;